import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { PlanOptionNameEnum } from "@platform/openapi-types";
import React, { useEffect, useState } from "react";
import { Typography } from "@platform/design-system";

import { useUpdateBenefitPlanOptionName } from "../../../../api/benefitPlanGroupMutations";
import {
  formatEnumValue,
  PLAN_LEVEL_ORDER,
} from "../../utilities/enumFormatters";

import type { CustomHeaderGroupProps } from "ag-grid-react";

export interface PlanLevelHeaderParams {
  rfpId: string;
  benefitPlanId: string;
  optionName?: PlanOptionNameEnum;
}

type Props = CustomHeaderGroupProps & PlanLevelHeaderParams;

const NOT_APPLICABLE_HELPER_TEXT =
  "Select when plan options differ by eligibility class";

/**
 * Inline "Plan Level" dropdown header (BTX only). Replaces the old
 * option+identifier combined header label and the popover-based
 * EditablePlanOptionHeader — this renders as a plain MUI Select so it
 * behaves like every other inline dropdown on the grid.
 */
const PlanLevelHeader = ({
  rfpId,
  benefitPlanId,
  optionName,
}: Props): React.JSX.Element => {
  const [value, setValue] = useState<PlanOptionNameEnum | "">(optionName ?? "");
  const [menuOpen, setMenuOpen] = useState(false);
  const updateOptionNameMutation = useUpdateBenefitPlanOptionName();
  const isMissing = value === "";

  // Re-sync when the persisted value changes (query invalidation, an
  // external edit) so the dropdown never shows a stale level.
  useEffect(() => {
    setValue(optionName ?? "");
  }, [optionName]);

  const handleChange = (
    event: SelectChangeEvent<PlanOptionNameEnum | "">,
  ): void => {
    const previous = value;
    const nextOptionName = event.target.value as PlanOptionNameEnum;
    setValue(nextOptionName); // optimistic

    updateOptionNameMutation.mutate(
      {
        rfpId,
        benefitPlanId,
        optionName: nextOptionName,
      },
      {
        // Revert the optimistic selection if the PATCH fails, so the
        // header doesn't show a level the server never persisted.
        onError: () => {
          setValue(previous);
        },
      },
    );
  };

  return (
    <Select<PlanOptionNameEnum | "">
      data-testid="plan-level-header-select"
      aria-label="Plan Level"
      value={value}
      onChange={handleChange}
      onOpen={() => setMenuOpen(true)}
      onClose={() => setMenuOpen(false)}
      // Serializes selections: a second change can't fire until the first
      // PATCH settles, so out-of-order responses can't leave a stale value
      // persisted (mirrors CurrentPolicyGroupHeader's disabled-while-pending).
      disabled={updateOptionNameMutation.isPending}
      displayEmpty
      variant="standard"
      disableUnderline
      fullWidth
      renderValue={(selected) => (selected ? formatEnumValue(selected) : "")}
      sx={{
        width: "100%",
        fontSize: "inherit",
        backgroundColor: "inherit",
        ...(isMissing && {
          color: "error.main",
          "& .MuiSelect-icon": { color: "error.main" },
        }),
      }}
    >
      {PLAN_LEVEL_ORDER.map((option) => (
        <MenuItem key={option} value={option}>
          {option === PlanOptionNameEnum.notApplicable && menuOpen ? (
            <Box>
              <Typography variant="body-medium">
                {formatEnumValue(option)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {NOT_APPLICABLE_HELPER_TEXT}
              </Typography>
            </Box>
          ) : (
            formatEnumValue(option)
          )}
        </MenuItem>
      ))}
    </Select>
  );
};

PlanLevelHeader.displayName = "PlanLevelHeader";
export default PlanLevelHeader;
